import classNames from "classnames";
import { forIn } from "lodash";
import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import { useSession } from "PFApp/use_session";
import UploadIcon from "PFIcons/upload.svg";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./file_upload.less";

// TODO: [PROF-1815] Refactor FileUpload component
export const FileUpload = ({
  url,
  name,
  headers,
  data,
  handleChange,
  handleLoad,
  handleError,
  handleNetworkError,
  allowedFileTypes,
  acceptMediaTypes,
  handleTypeError,
  qaId,
  style,
  inputRef,
  classes,
  showDraggingHint,
  disabled,
  children
}) => {
  const {
    store: { currentAccount }
  } = useContext(AppContext);
  const { getAccessToken } = useSession();
  const { t } = useTranslation("core");
  const growl = useGrowl();

  const [dragging, setDragging] = useState(false);

  const requestHeaders = headers || {
    ACCOUNT: currentAccount.full_domain,
    ACCEPT: "application/vnd.profinda+json;version=2",
    Authorization: `Bearer ${getAccessToken()}`
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);

    const file = e.dataTransfer ? e.dataTransfer.files[0] : null;

    if (!file) {
      return;
    }

    validateFileType(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return; // when cancel button pressed in filedialog
    }
    e.target.value = null; // reset so you can trigger change multiple times

    validateFileType(file);
  };

  const validateFileType = (file) => {
    const isValidType = allowedFileTypes?.test(file.name);
    if (!allowedFileTypes) {
      handleChangeFunction(file);
    } else if (isValidType) {
      handleChangeFunction(file);
    } else {
      handleTypeError();
    }
  };

  const handleChangeFunction = (file) => {
    const formData = new FormData();

    forIn(data, (value, key) => {
      formData.append(key, value);
    });

    formData.append(name, new Blob([file]), file.name);

    const req = new XMLHttpRequest();
    req.open("POST", url);

    for (var key in requestHeaders) {
      req.setRequestHeader(key, requestHeaders[key]);
    }

    handleChange && handleChange();

    req.onload = () => {
      if (req.status <= 201) {
        handleLoad && handleLoad(JSON.parse(req.response));
      } else {
        handleError && handleError(JSON.parse(req.response));
      }
    };

    req.onerror = () => {
      handleNetworkError();
      growl({ kind: "error", message: t("errors.networkError") });
    };

    req.send(formData);
  };

  return (
    <div
      className={classNames(css.root, { [css.dragging]: dragging }, classes?.root)}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      aria-hidden
    >
      <label className={classNames(css.label, classes?.label)} htmlFor="file-upload-input">
        <div className={classNames(css.wrapper, classes?.content)}>{children}</div>
        <input
          id="file-upload-input"
          ref={inputRef}
          onChange={handleInputChange}
          name={name}
          type="file"
          accept={acceptMediaTypes?.join(",")}
          disabled={disabled}
          data-qa-id={qaId}
          style={style}
        />
      </label>
      {dragging && (
        <div className={css.overlay} onDragLeave={handleDragLeave} aria-hidden>
          {showDraggingHint && (
            <>
              <div className={css.uploadIcon}>
                <UploadIcon width={200} height={200} />
              </div>
              <h2>{t("dropTheFilesHere")}</h2>
            </>
          )}
        </div>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  children: PropTypes.node.isRequired,
  url: PropTypes.string.isRequired,
  headers: PropTypes.object,
  data: PropTypes.object,
  name: PropTypes.string.isRequired,
  inputRef: PropTypes.object,

  qaId: PropTypes.string,
  style: PropTypes.object,

  handleChange: PropTypes.func,
  handleLoad: PropTypes.func,
  handleError: PropTypes.func,
  handleNetworkError: PropTypes.func,
  handleTypeError: PropTypes.func,
  showDraggingHint: PropTypes.bool,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string
  }),

  // Pass a regex to check if is allowed type
  // eg: /\.(jpg|jpeg|png|pdf)$/i
  allowedFileTypes: PropTypes.instanceOf(RegExp),
  // eg: ["image/png", ".csv"]
  acceptMediaTypes: PropTypes.arrayOf(PropTypes.string)
};

FileUpload.defaultProps = {
  // by default FileUpload renders invisible (but no display:none to ensure it responds
  // to change events properly) - it's supposed to be wrapped in a <label> tag
  style: { clip: "rect(0,0,0,0)", opacity: 0, position: "absolute" },
  allowedFileTypes: null,
  showDraggingHint: true,
  data: {}
};
